import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLang, pathForLang } from "../i18n";

const SITE = "https://stellapolariscoaching.com";

/**
 * Keeps <html lang>, <title>, meta description, canonical, OG image, and
 * hreflang alternates in sync with the current route + language. Search
 * engines that execute JS will pick these up; the static defaults in
 * /app/frontend/public/index.html cover crawlers that don't.
 *
 * Pass { title, description } from the calling page to override the brand
 * defaults. `getAltPath(lang)` lets a page declare the URL of its translated
 * mirror (e.g. an article's slug differs by language).
 */
export const useHeadTags = ({
  title,
  description,
  image,
  getAltPath,
} = {}) => {
  const lang = useLang();
  const loc = useLocation();

  useEffect(() => {
    document.documentElement.lang = lang;

    const pathEn = getAltPath ? getAltPath("en") : pathForLang("en", loc.pathname);
    const pathEs = getAltPath ? getAltPath("es") : pathForLang("es", loc.pathname);
    const canonicalUrl = `${SITE}${lang === "en" ? pathEn : pathEs}`;

    if (title) document.title = title;

    const upsert = (selector, create) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = create();
        document.head.appendChild(el);
      }
      return el;
    };

    const setMeta = (selector, attr, value, build) => {
      if (!value) return;
      const el = upsert(selector, build);
      el.setAttribute(attr, value);
    };

    setMeta(
      'meta[name="description"]',
      "content",
      description,
      () => {
        const m = document.createElement("meta");
        m.setAttribute("name", "description");
        return m;
      }
    );
    setMeta(
      'meta[property="og:title"]',
      "content",
      title,
      () => {
        const m = document.createElement("meta");
        m.setAttribute("property", "og:title");
        return m;
      }
    );
    setMeta(
      'meta[property="og:description"]',
      "content",
      description,
      () => {
        const m = document.createElement("meta");
        m.setAttribute("property", "og:description");
        return m;
      }
    );
    setMeta(
      'meta[property="og:url"]',
      "content",
      canonicalUrl,
      () => {
        const m = document.createElement("meta");
        m.setAttribute("property", "og:url");
        return m;
      }
    );
    setMeta(
      'meta[name="twitter:title"]',
      "content",
      title,
      () => {
        const m = document.createElement("meta");
        m.setAttribute("name", "twitter:title");
        return m;
      }
    );
    setMeta(
      'meta[name="twitter:description"]',
      "content",
      description,
      () => {
        const m = document.createElement("meta");
        m.setAttribute("name", "twitter:description");
        return m;
      }
    );
    if (image) {
      const ogImg = upsert('meta[property="og:image"]', () => {
        const m = document.createElement("meta");
        m.setAttribute("property", "og:image");
        return m;
      });
      ogImg.setAttribute("content", image);
      const twImg = upsert('meta[name="twitter:image"]', () => {
        const m = document.createElement("meta");
        m.setAttribute("name", "twitter:image");
        return m;
      });
      twImg.setAttribute("content", image);
    }

    const canonical = upsert('link[rel="canonical"]', () => {
      const l = document.createElement("link");
      l.setAttribute("rel", "canonical");
      return l;
    });
    canonical.setAttribute("href", canonicalUrl);

    const altEn = upsert('link[rel="alternate"][hreflang="en"]', () => {
      const l = document.createElement("link");
      l.setAttribute("rel", "alternate");
      l.setAttribute("hreflang", "en");
      return l;
    });
    altEn.setAttribute("href", `${SITE}${pathEn}`);

    const altEs = upsert('link[rel="alternate"][hreflang="es"]', () => {
      const l = document.createElement("link");
      l.setAttribute("rel", "alternate");
      l.setAttribute("hreflang", "es");
      return l;
    });
    altEs.setAttribute("href", `${SITE}${pathEs}`);

    const altDefault = upsert(
      'link[rel="alternate"][hreflang="x-default"]',
      () => {
        const l = document.createElement("link");
        l.setAttribute("rel", "alternate");
        l.setAttribute("hreflang", "x-default");
        return l;
      }
    );
    altDefault.setAttribute("href", `${SITE}${pathEn}`);
  }, [lang, loc.pathname, title, description, image, getAltPath]);
};
